import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { DocsToc, SpecDownload } from "../components";
import { DocsTocItem } from "../interfaces";
import { API_BASE_URL } from "../utils";

const TOC_ITEMS: DocsTocItem[] = [
  { id: "overview-rules", translationKey: "docs.overview.rulesTitle", level: 1 },
  {
    id: "overview-base-url",
    translationKey: "docs.overview.baseUrlTitle",
    level: 1,
  },
  { id: "overview-downloads", translationKey: "docs.spec.title", level: 1 },
  { id: "overview-next", translationKey: "docs.overview.nextTitle", level: 1 },
];

/**
 * YOSHI-10464: the rules of the contract, as a table: the fact, and nothing around it.
 *
 * Every one of them is something a biller gets WRONG at a cost if nobody says it: which sales are
 * counted, that the payments include attempts that never cleared, and that the tax split assumes a
 * single rate. They are stated here rather than buried in a field description because by the time a
 * reader is looking up a field, the invoice is already built.
 */
const RULES: string[] = ["closedEvents", "salesCounted", "money", "reconcile", "paymentAttempts", "taxSplit"];

export const Overview = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col-reverse gap-8 lg:flex-row lg:gap-12">
      <div className="min-w-0 flex-1 space-y-12">
        <div className="space-y-4">
          <h2 className="text-xl font-bold lg:text-2xl">
            {t("docs.overview.title")}
          </h2>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.overview.intro")}
          </p>
        </div>

        <div className="space-y-4">
          <h3
            id="overview-rules"
            className="scroll-mt-6 text-lg font-bold lg:text-xl"
          >
            {t("docs.overview.rulesTitle")}
          </h3>
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[30rem] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th
                    scope="col"
                    className="py-2 pr-4 font-semibold text-gray-700"
                  >
                    {t("docs.common.rule")}
                  </th>
                  <th scope="col" className="py-2 font-semibold text-gray-700">
                    {t("docs.common.meaning")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((rule) => (
                  <tr key={rule} className="border-b border-gray-100 align-top">
                    <td className="py-3 pr-4 font-semibold text-gray-700">
                      {t(`docs.overview.rules.${rule}.title`)}
                    </td>
                    <td className="py-3 leading-relaxed text-gray-500">
                      {t(`docs.overview.rules.${rule}.content`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* YOSHI-10464: a single address, so this is one statement and not a table of servers. */}
        <div className="space-y-4">
          <h3
            id="overview-base-url"
            className="scroll-mt-6 text-lg font-bold lg:text-xl"
          >
            {t("docs.overview.baseUrlTitle")}
          </h3>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.overview.baseUrlContent")}
          </p>
          <code className="block break-all font-mono text-sm text-primary">
            {API_BASE_URL}
          </code>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.overview.baseUrlTesting")}
          </p>
        </div>

        <div id="overview-downloads" className="scroll-mt-6">
          <SpecDownload />
        </div>

        <div className="space-y-4">
          <h3
            id="overview-next"
            className="scroll-mt-6 text-lg font-bold lg:text-xl"
          >
            {t("docs.overview.nextTitle")}
          </h3>
          <p className="text-base/relaxed text-gray-400 lg:text-lg/relaxed">
            {t("docs.overview.nextContent")}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              id="docs-overview-quickstart"
              to="/docs/quickstart"
              className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              {t("docs.nav.quickstart")}
            </Link>
            <Link
              id="docs-overview-endpoints"
              to="/docs/endpoints"
              className="rounded-md border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              {t("docs.nav.endpoints")}
            </Link>
          </div>
        </div>
      </div>
      <DocsToc items={TOC_ITEMS} />
    </div>
  );
};
